import { Box } from "@mui/material";
import PropTypes from "prop-types";
import { INK, withAlpha } from "../../../theme/tokens";

/*
 * TechBackground
 * A premium, living backdrop for the marketing sections:
 *  - a softly tinted (not pure white) base
 *  - a multi-hue gradient MESH around the brand green — yellow-green → green →
 *    teal → cyan → soft blue — that slowly FLOWS and BREATHES
 *  - two drifting colored glows for depth and rhythm
 *  - an optional faint dot grid that fades at the edges (`grid`)
 *  - a barely-there grain layer so large areas don't band flatly
 *
 * Props:
 *  - grid:  show the subtle dot grid overlay (default false)
 *  - vivid: make the flowing color more obvious (default false)
 *
 * Motion is slow and GPU-friendly and disables under `prefers-reduced-motion`.
 * Decorative; sits behind content (zIndex 0); no pointer events.
 */

// Aurora palette — analogous hues centered on the brand green.
const LIME = "#A6CF7E"; // yellow-green
const GREEN = "#3F9A82"; // brand
const TEAL = "#2F8F84"; // deep teal
const CYAN = "#2FA6C2"; // teal-blue
const BLUE = "#5B8DEF"; // soft blue

const GRAIN =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")";

const EDGE_MASK =
  "radial-gradient(ellipse 80% 62% at 50% 30%, #000 42%, transparent 84%)";

const REDUCE = "@media (prefers-reduced-motion: reduce)";

const TechBackground = ({ grid = false, vivid = false }) => {
  const a = (base) => withAlpha(base[0], vivid ? base[2] : base[1]);
  // [hex, normal alpha, vivid alpha]
  const mesh = [
    ["18% 28%", "40% 50%", [LIME, 0.34, 0.52]],
    ["80% 22%", "44% 52%", [GREEN, 0.3, 0.46]],
    ["66% 80%", "48% 56%", [CYAN, 0.26, 0.42]],
    ["28% 76%", "42% 52%", [BLUE, 0.22, 0.36]],
    ["50% 50%", "38% 46%", [TEAL, 0.2, 0.34]],
  ];

  return (
    <Box
      aria-hidden
      sx={{
        position: "absolute",
        inset: 0,
        overflow: "hidden",
        zIndex: 0,
        pointerEvents: "none",
        "@keyframes tb-flow": {
          "0%": { backgroundPosition: "0% 50%" },
          "50%": { backgroundPosition: "100% 50%" },
          "100%": { backgroundPosition: "0% 50%" },
        },
        "@keyframes tb-breathe": {
          "0%": { transform: "scale(1)", opacity: vivid ? 0.95 : 0.9 },
          "100%": { transform: "scale(1.1)", opacity: 1 },
        },
        "@keyframes tb-floatA": {
          "0%": { transform: "translate(0,0) scale(1)" },
          "100%": { transform: "translate(46px,32px) scale(1.1)" },
        },
        "@keyframes tb-floatB": {
          "0%": { transform: "translate(0,0) scale(1)" },
          "100%": { transform: "translate(-52px,-32px) scale(1.12)" },
        },
      }}
    >
      {/* Softly tinted base (not white) */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(180deg, #F3F8F4 0%, #E9F1EE 55%, #E2ECEF 100%)",
        }}
      />

      {/* Flowing + breathing multi-hue gradient mesh */}
      <Box
        sx={{
          position: "absolute",
          inset: "-12%",
          backgroundImage: mesh
            .map(
              ([at, size, base]) =>
                `radial-gradient(${size} at ${at}, ${a(base)}, transparent 70%)`,
            )
            .join(","),
          backgroundSize: "200% 200%",
          filter: vivid ? "blur(44px)" : "blur(48px)",
          animation:
            "tb-flow 22s ease-in-out infinite, tb-breathe 11s ease-in-out infinite alternate",
          [REDUCE]: { animation: "none" },
        }}
      />

      {/* Yellow-green glow (top-left, drifting) */}
      <Box
        sx={{
          position: "absolute",
          top: "-12%",
          left: "-8%",
          width: { xs: 380, md: 620 },
          height: { xs: 380, md: 620 },
          borderRadius: "50%",
          filter: "blur(100px)",
          opacity: vivid ? 0.6 : 0.45,
          background: `radial-gradient(closest-side, ${withAlpha(
            LIME,
            vivid ? 0.55 : 0.4,
          )}, transparent 70%)`,
          animation: "tb-floatA 22s ease-in-out infinite alternate",
          [REDUCE]: { animation: "none" },
        }}
      />

      {/* Blue / cyan glow (bottom-right, drifting) */}
      <Box
        sx={{
          position: "absolute",
          bottom: "-18%",
          right: "-8%",
          width: { xs: 420, md: 700 },
          height: { xs: 420, md: 700 },
          borderRadius: "50%",
          filter: "blur(110px)",
          opacity: vivid ? 0.55 : 0.4,
          background: `radial-gradient(closest-side, ${withAlpha(
            BLUE,
            vivid ? 0.42 : 0.3,
          )}, transparent 70%)`,
          animation: "tb-floatB 28s ease-in-out infinite alternate",
          [REDUCE]: { animation: "none" },
        }}
      />

      {/* Optional faint line grid, masked to fade at the edges */}
      {grid && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage: [
              `linear-gradient(${withAlpha(INK, 0.06)} 1px, transparent 1px)`,
              `linear-gradient(90deg, ${withAlpha(INK, 0.06)} 1px, transparent 1px)`,
            ].join(","),
            backgroundSize: "40px 40px",
            maskImage: EDGE_MASK,
            WebkitMaskImage: EDGE_MASK,
          }}
        />
      )}

      {/* Fine grain for a premium, non-flat surface */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          opacity: 0.035,
          mixBlendMode: "multiply",
          backgroundImage: GRAIN,
          backgroundSize: "180px 180px",
        }}
      />
    </Box>
  );
};

TechBackground.propTypes = {
  grid: PropTypes.bool,
  vivid: PropTypes.bool,
};

export default TechBackground;
